import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import StatsAdmin from '../components/admin/StatsAdmin';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiSettings, FiAlertTriangle, FiBarChart2, FiDatabase, FiLayout, FiFlag, FiMessageSquare } = FiIcons;

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');

  // Redirect non-admin users
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <SafeIcon icon={FiAlertTriangle} className="h-16 w-16 text-patriot-red mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-patriot-navy mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access the admin dashboard.</p>
          <a href="/" className="bg-patriot-red text-white px-4 py-2 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-patriot-navy mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">
            Manage platform content, users, and settings
          </p>
        </motion.div>

        {/* Admin Navigation */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex space-x-2 min-w-max">
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === 'stats' ? 'bg-patriot-red text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiBarChart2} className="h-4 w-4 mr-2" /> Platform Statistics
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === 'users' ? 'bg-patriot-red text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiUsers} className="h-4 w-4 mr-2" /> User Management
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === 'content' ? 'bg-patriot-red text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiLayout} className="h-4 w-4 mr-2" /> Content Management
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === 'reports' ? 'bg-patriot-red text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiFlag} className="h-4 w-4 mr-2" /> Reported Content
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === 'settings' ? 'bg-patriot-red text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiSettings} className="h-4 w-4 mr-2" /> System Settings
            </button>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Statistics Tab */}
          {activeTab === 'stats' && <StatsAdmin />}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold text-patriot-navy mb-6">User Management</h2>
              <div className="text-center py-16 text-gray-500">
                <SafeIcon icon={FiUsers} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>User management module will be implemented here.</p>
              </div>
            </motion.div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold text-patriot-navy mb-6">Content Management</h2>
              <div className="text-center py-16 text-gray-500">
                <SafeIcon icon={FiLayout} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>Content management module will be implemented here.</p>
              </div>
            </motion.div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold text-patriot-navy mb-6">Reported Content</h2>
              <div className="text-center py-16 text-gray-500">
                <SafeIcon icon={FiFlag} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>Reported content module will be implemented here.</p>
              </div>
            </motion.div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold text-patriot-navy mb-6">System Settings</h2>
              <div className="text-center py-16 text-gray-500">
                <SafeIcon icon={FiSettings} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>System settings module will be implemented here.</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;